import { getCSRFToken } from "./htmx-helpers";
import { setActiveWikiEditor, clearActiveWikiEditor } from "./wiki-nav";

export interface WikiAutosaveConfig {
  pageId: string;
  saveContentUrl: string;
  syncLinksUrl: string;
  initialUpdatedAt: string;
  /** Wiki root URL (scope.WikiURL()) — used to rewrite the address bar after a slug rename. */
  wikiBaseUrl?: string;
  /** Localized toast when a navigation is blocked because the save failed. */
  saveFailedMessage?: string;
}

type SaveStatus = "saved" | "unsaved" | "saving" | "error" | "conflict";

const MIN_STATUS_DURATION_MS = 500;

export function wikiAutosave(config: WikiAutosaveConfig) {
  return {
    _saveTimer: null as ReturnType<typeof setTimeout> | null,
    _status: "saved" as SaveStatus,
    _lastSavedAt: config.initialUpdatedAt,
    _hiddenInput: null as HTMLInputElement | null,
    // Save-gate bookkeeping (see wiki-nav.ts): the in-flight PATCH and
    // whether links changed since the last sync-links.
    _inflight: null as Promise<void> | null,
    _linksDirty: false,
    _listeners: null as AbortController | null,
    saveFailedMessage: config.saveFailedMessage ?? "",

    // Display status with minimum duration to prevent flicker
    _displayStatus: "saved" as SaveStatus,
    _displayStatusSetAt: 0,
    _displayTimer: null as ReturnType<typeof setTimeout> | null,

    // Content stats
    charCount: 0,
    wordCount: 0,

    get displayStatus(): SaveStatus {
      return this._displayStatus;
    },

    _setDisplayStatus(status: SaveStatus) {
      const now = Date.now();
      const elapsed = now - this._displayStatusSetAt;
      const remaining = MIN_STATUS_DURATION_MS - elapsed;

      if (this._displayTimer) {
        clearTimeout(this._displayTimer);
        this._displayTimer = null;
      }

      if (remaining > 0 && this._displayStatusSetAt > 0) {
        // Delay the transition so current state shows for at least MIN_STATUS_DURATION_MS
        this._displayTimer = setTimeout(() => {
          this._displayStatus = status;
          this._displayStatusSetAt = Date.now();
        }, remaining);
      } else {
        this._displayStatus = status;
        this._displayStatusSetAt = now;
      }
    },

    _updateContentStats() {
      const content = this._hiddenInput?.value ?? "";
      this.charCount = content.length;
      this.wordCount = content.trim() ? content.trim().split(/\s+/).length : 0;
    },

    init() {
      // Find the hidden input inside the markdown editor.
      // It's inside a nested x-data scope (markdownEditor), so $refs won't find it.
      const el = (this as any).$el as HTMLElement;
      this._hiddenInput = el.querySelector('input[name="content"]') as HTMLInputElement;

      // Initial content stats
      this._updateContentStats();

      if (this._hiddenInput) {
        this._hiddenInput.addEventListener("markdown-change", () => {
          this._updateContentStats();
          this._status = "unsaved";
          this._linksDirty = true;
          this._setDisplayStatus("unsaved");
          this.debounceSave();
        });
      }

      // Window/document listeners outlive this component (boosted navigation
      // swaps the body, it doesn't unload the page) — tie them to destroy().
      this._listeners = new AbortController();
      const signal = this._listeners.signal;

      window.addEventListener(
        "beforeunload",
        () => {
          this._flushOnUnload();
        },
        { signal },
      );

      document.addEventListener(
        "keydown",
        (e: KeyboardEvent) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "s") {
            e.preventDefault();
            this.saveNow();
            this.syncLinks();
          }
        },
        { signal },
      );

      // Listen for meta updates that bump updated_at
      window.addEventListener(
        "wiki-meta-saved",
        ((e: CustomEvent) => {
          if (e.detail.updatedAt) this._lastSavedAt = e.detail.updatedAt;
        }) as EventListener,
        { signal },
      );

      // Wiki links are boosted <a href>s; the save-gate holds them while this
      // editor has pending work (wiki-nav.ts).
      setActiveWikiEditor(this as any);
    },

    destroy() {
      this._listeners?.abort();
      this._listeners = null;
      clearActiveWikiEditor(this as any);
      // Left through an ungated path (app nav link, page create's HX-Location,
      // Back/Forward): persist now instead of waiting for the debounce. The
      // detached hidden input still holds the latest value.
      if (this.hasPendingChanges()) void this.flush();
    },

    hasPendingChanges(): boolean {
      return (
        this._status !== "saved" ||
        this._saveTimer !== null ||
        this._inflight !== null ||
        this._linksDirty
      );
    },

    // Persist everything before a navigation: cancel the debounce, wait for an
    // in-flight save, save again while the content still differs from what
    // was persisted, then sync links. Resolves true when the content is saved
    // (a failed link sync doesn't block — the content is safe).
    async flush(): Promise<boolean> {
      if (this._saveTimer) {
        clearTimeout(this._saveTimer);
        this._saveTimer = null;
      }
      for (let attempt = 0; attempt < 3; attempt++) {
        if (this._inflight) await this._inflight;
        if (this._status === "saved") break;
        if (this._status === "conflict") return false;
        await this.saveContent();
        // (re-read after the await — TS keeps the pre-await narrowing)
        const after = this._status as SaveStatus;
        if (after === "error" || after === "conflict") return false;
      }
      if ((this._status as SaveStatus) !== "saved") return false;
      if (this._linksDirty) {
        try {
          await this.syncLinks();
        } catch {
          // best effort — links resync on the next save/Ctrl+S
        }
      }
      return true;
    },

    debounceSave() {
      if (this._saveTimer) clearTimeout(this._saveTimer);
      this._saveTimer = setTimeout(() => {
        this._saveTimer = null;
        this.saveContent();
      }, 3000);
    },

    saveContent(): Promise<void> {
      // Serialize saves: a second PATCH racing the first would carry a stale
      // updated_at and 409.
      const run = this._inflight
        ? this._inflight.then(() => this._saveContentOnce())
        : this._saveContentOnce(); // idle: the PATCH leaves synchronously
      const tracked = run.finally(() => {
        if (this._inflight === tracked) this._inflight = null;
      });
      this._inflight = tracked;
      return tracked;
    },

    async _saveContentOnce() {
      this._status = "saving";
      this._setDisplayStatus("saving");
      const content = this._hiddenInput?.value ?? "";
      try {
        const resp = await fetch(config.saveContentUrl, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": getCSRFToken(),
          },
          body: JSON.stringify({
            content,
            updated_at: this._lastSavedAt,
          }),
        });
        if (resp.status === 409) {
          this._status = "conflict";
          this._setDisplayStatus("conflict");
          return;
        }
        if (!resp.ok) {
          this._status = "error";
          this._setDisplayStatus("error");
          return;
        }
        const updatedAt = resp.headers.get("X-Updated-At");
        if (updatedAt) this._lastSavedAt = updatedAt;
        // An edit typed while this PATCH was in flight is still unsaved.
        if ((this._hiddenInput?.value ?? "") !== content) {
          this._status = "unsaved";
          this._setDisplayStatus("unsaved");
          return;
        }
        this._status = "saved";
        this._setDisplayStatus("saved");
      } catch {
        this._status = "error";
        this._setDisplayStatus("error");
      }
    },

    async saveNow() {
      if (this._saveTimer) {
        clearTimeout(this._saveTimer);
        this._saveTimer = null;
      }
      await this.saveContent();
    },

    async syncLinks() {
      this._linksDirty = false;
      try {
        const resp = await fetch(config.syncLinksUrl, {
          method: "POST",
          headers: { "X-CSRF-Token": getCSRFToken() },
        });
        if (!resp.ok) this._linksDirty = true;
      } catch {
        this._linksDirty = true;
      }
    },

    _flushOnUnload() {
      // If there are unsaved changes, fire a synchronous XHR as last resort.
      // sendBeacon can't carry CSRF headers, so we use sync XHR (deprecated but
      // it's the only reliable way to save during unload with custom headers).
      // If this fails, the consistency safety net on next read handles it.
      if (this._status !== "unsaved" && this._saveTimer === null) return;
      try {
        const xhr = new XMLHttpRequest();
        xhr.open("PATCH", config.saveContentUrl, false); // synchronous
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.setRequestHeader("X-CSRF-Token", getCSRFToken());
        xhr.send(
          JSON.stringify({
            content: this._hiddenInput?.value ?? "",
            updated_at: this._lastSavedAt,
          }),
        );
      } catch {
        // Best effort — safety net on next read will catch it
      }
    },

    // Called from the template for meta updates
    async updateMeta(data: Record<string, unknown>) {
      const metaUrl = config.saveContentUrl.replace("/content", "/meta");
      const resp = await fetch(metaUrl, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCSRFToken(),
        },
        body: JSON.stringify(data),
      });
      if (resp.ok) {
        const ts = resp.headers.get("X-Updated-At");
        if (ts) this._lastSavedAt = ts;

        // If title changed, update the URL to the new slug without reload.
        // Scope-aware (studio /assistant/wiki vs org /organizations/{slug}/wiki).
        const newSlug = resp.headers.get("X-Wiki-Slug");
        if (newSlug) {
          const base = config.wikiBaseUrl ?? window.location.pathname.replace(/\/[^/]*$/, "");
          const target = base + "/" + encodeURIComponent(newSlug);
          if (window.location.pathname !== target) {
            // Keep htmx's own history-state marker so Back still restores via htmx.
            window.history.replaceState(window.history.state, "", target);
          }
        }

        // Refresh sidebar to reflect meta changes
        document.body.dispatchEvent(new CustomEvent("wiki-sidebar-refresh"));
      }
    },
  };
}
